// ============================================================================
// FSA REPORT MODAL
// ============================================================================
// Per-engagement editor for one NARRATIVE report (Policy / Auditor's /
// Director's / CARO). Side-by-side layout:
//
//   Left  — placeholder form. Auto-source placeholders are seeded from
//           engagement context on first hydrate; once any value is saved
//           the auditor's value wins.
//   Right — live preview. Body HTML rendered with placeholders substituted
//           from the editor state. Unresolved tokens render as an amber
//           chip so the auditor sees exactly what's still missing.
//
// CONTENT MODES — each report is authored either from the pinned narrative
// TEMPLATE (above) or as FREE TEXT: one big editor where the auditor types
// or pastes the complete report body (the firm's own Word draft). Free text
// keeps all the generated chrome (letterhead, footer, signature block); in
// the body, blank line = paragraph and a short ALL-CAPS line = section
// heading. Switching to Free Text with an empty body seeds the editor from
// the current template output (placeholders substituted, headings
// uppercased) so nothing starts from a blank page. The choice + body live
// in the same saved workspace blob (content_mode / free_text).
//
// Save is dirty-tracked against the hydrated snapshot. Print is exposed
// via the PDF preview modal (blob-iframe). Submit / Approve / Reopen
// transitions land in FSA-2.3 alongside the rest of the FS finalisation
// flow — for now everything stays in DRAFT.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { fsaReportService } from 'api/services/fsa/fsaReportService';
import { useFsaReportHydrate } from 'hooks/api/fsa/useFsaReports';
import { useFsaCorporateFacts, parseCorporateFactsData } from 'hooks/api/fsa/useFsaCorporateFacts';
import { fsaCorporateFactsService } from 'api/services/fsa/fsaCorporateFactsService';
import FsaReportPdfPreviewModal from 'components/modals/modules/fsa/FsaReportPdfPreviewModal';
import SectionedReportPane from 'components/modals/modules/fsa/reports/SectionedReportPane';
import { loadPriorSections } from 'components/modals/modules/fsa/reports/priorSections';
import { FULLSCREEN_STYLE, FullscreenButton, useModalFullscreen } from 'components/modals/modules/fsa/reports/useModalFullscreen';
import { seedResolvedData } from 'components/modals/modules/fsa/reports/reportSections';
import type { ID } from 'api/types';
import type {
  PlaceholderDescriptor,
  ReportWorkspaceData,
} from 'api/types/fsa/reportData.types';

interface Props {
  isOpen: boolean;
  engagementId: ID;
  reportId: ID | null;
  /** Pre-fetched header so the title shows immediately while hydrate fetches. */
  reportHeader?: { code: string; name: string };
  onClose: () => void;
  onSaved?: () => void;
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (reportId: ID) => void;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const EMPTY_WORKSPACE: ReportWorkspaceData = { placeholders: {}, notes_text: '' };

const FsaReportModal: React.FC<Props> = ({
  isOpen,
  engagementId,
  reportId,
  reportHeader,
  onClose,
  onSaved,
  tabId,
  moduleName,
  onRestoreModal,
  minimizeRef,
}) => {
  const { data: hydrate, loading: hydrating, refetch: refetchHydrate } =
    useFsaReportHydrate(engagementId, isOpen ? reportId : null);

  // ───── form state ─────────────────────────────────────────────────────────
  const [workspace, setWorkspace] = useState<ReportWorkspaceData>(EMPTY_WORKSPACE);
  const workspaceRef = useRef(workspace);
  workspaceRef.current = workspace;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  // ADR-0024 — SECTIONS components (the sectioned Board's Report) use the
  // shared section editor instead of the placeholder form. Their variants
  // follow the Corporate Facts applicability answers.
  const isSections = hydrate?.contentFormat === 'SECTIONS';
  const { data: corporateFacts, refetch: refetchFacts } = useFsaCorporateFacts(isOpen && isSections ? engagementId : null);
  const corporateFactsData = useMemo(
    () => (corporateFacts ? parseCorporateFactsData(corporateFacts.data) : undefined),
    [corporateFacts],
  );

  // ADR-0024 A-1 — report questions (audit scope) are saved straight to the
  // engagement's facts, then re-read so the variants switch at once.
  const handleSetAuditAnswer = useCallback(async (code: string, value: boolean | string | number | null, scope: 'audit' | 'note' | 'inputs' | 'standing') => {
    try {
      await fsaCorporateFactsService.setAuditAnswers(engagementId, { [code]: value }, scope);
      await refetchFacts();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not save the answer.');
    }
  }, [engagementId, refetchFacts]);
  const reportCode = (reportHeader?.code ?? hydrate?.reportCode ?? '').toUpperCase();
  // The report's template row says which side signs (a report added later,
  // e.g. List of Shareholders, is signed by the Board); the codes are the fallback.
  const signingSide: 'board' | 'auditor' | 'none' = hydrate?.signingSide
    ?? (reportCode === 'AUDIT_REPORT' || reportCode === 'CARO' ? 'auditor'
      : reportCode === 'DIRECTORS_REPORT' ? 'board'
        : 'none');
  // Bumped each time saved data is loaded — the section editor re-creates
  // TipTap on it (TipTap reads its content only at creation).
  const [sectionsContentVersion, setSectionsContentVersion] = useState(0);

  // Hydrate effect — seed the form from saved data (or auto-resolved
  // values for any placeholder the auditor hasn't touched yet). Skips
  // when restoring from minimize.
  useEffect(() => {
    if (!isOpen || !hydrate) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      return;
    }
    const seeded = seedWorkspace(hydrate);
    setWorkspace(seeded);
    initialSnapshotRef.current = JSON.stringify(seeded);
    setSectionsContentVersion(v => v + 1);
  }, [isOpen, hydrate]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(workspace) !== initialSnapshotRef.current;
  }, [workspace]);

  // ───── parsed descriptors ─────────────────────────────────────────────────
  const descriptors = useMemo<PlaceholderDescriptor[]>(() => {
    if (!hydrate?.placeholdersJson) return [];
    try {
      const parsed = JSON.parse(hydrate.placeholdersJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  }, [hydrate?.placeholdersJson]);

  // Resolved value map for the live preview = auto-source overridden by
  // current editor state. Mirrors the backend PDF resolver exactly.
  const resolvedValues = useMemo(() => {
    const map: Record<string, string> = { ...(hydrate?.autoResolvedValues ?? {}) };
    for (const [k, v] of Object.entries(workspace.placeholders ?? {})) {
      if (v != null && v !== '') map[k] = v;
    }
    return map;
  }, [hydrate?.autoResolvedValues, workspace.placeholders]);

  // ───── field setters ──────────────────────────────────────────────────────
  const setPlaceholder = useCallback((token: string, value: string) => {
    setWorkspace(prev => ({
      ...prev,
      placeholders: { ...prev.placeholders, [token]: value },
    }));
  }, []);
  const setNotesText = useCallback((text: string) => {
    setWorkspace(prev => ({ ...prev, notes_text: text }));
  }, []);
  const setFreeText = useCallback((text: string) => {
    setWorkspace(prev => ({ ...prev, free_text: text }));
  }, []);

  // ───── content mode ───────────────────────────────────────────────────────
  const contentMode = workspace.content_mode ?? 'TEMPLATE';
  const setContentMode = useCallback((mode: 'TEMPLATE' | 'FREE_TEXT') => {
    setWorkspace(prev => {
      if ((prev.content_mode ?? 'TEMPLATE') === mode) return prev;
      // First switch to Free Text with an empty body → seed from the
      // current template output so the auditor edits from the generated
      // text instead of a blank page. Placeholder values already typed
      // are substituted; unresolved tokens stay as {{TOKEN}} (the PDF
      // resolver still fills/flags them). Nothing is lost switching
      // back — both bodies live side by side in the workspace blob.
      if (mode === 'FREE_TEXT' && !(prev.free_text ?? '').trim() && hydrate?.bodyHtml) {
        const map: Record<string, string> = { ...(hydrate.autoResolvedValues ?? {}) };
        for (const [k, v] of Object.entries(prev.placeholders ?? {})) {
          if (v != null && v !== '') map[k] = v;
        }
        return { ...prev, content_mode: mode, free_text: templateHtmlToFreeText(hydrate.bodyHtml, map) };
      }
      return { ...prev, content_mode: mode };
    });
  }, [hydrate?.bodyHtml, hydrate?.autoResolvedValues]);

  // ───── save flow ──────────────────────────────────────────────────────────
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveClick = useCallback(() => {
    if (!isDirty) {
      notificationService.toast.info('No changes to save.');
      return;
    }
    setSaveConfirmOpen(true);
  }, [isDirty]);

  const handleConfirmSave = useCallback(async () => {
    if (!reportId) return;
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const payload = JSON.stringify(workspace);
      const r = await fsaReportService.save(engagementId, reportId, {
        data: payload,
        rowVersion: hydrate?.workspace?.rowVersion ?? null,
      });
      // Reset the dirty baseline to the just-saved blob so the user can
      // keep editing without the dialog yelling about discards.
      initialSnapshotRef.current = JSON.stringify(workspace);
      setIsSaving(false);
      notificationService.toast.success('Report saved.');
      onSaved?.();
      // Refresh hydrate so updated row_version + audit metadata reach the form.
      refetchHydrate();
      // Surface response for tooling (don't otherwise consume).
      void r;
    } catch (err: any) {
      setIsSaving(false);
      notificationService.toast.error(
        err?.response?.data?.message ?? 'Error saving report.',
      );
    }
  }, [reportId, engagementId, workspace, hydrate?.workspace?.rowVersion, onSaved, refetchHydrate]);

  const handleClose = useCallback(() => {
    if (isDirty) { setDiscardConfirmOpen(true); return; }
    onClose();
  }, [isDirty, onClose]);

  // ───── PDF preview ────────────────────────────────────────────────────────
  const [pdfOpen, setPdfOpen] = useState(false);

  // ───── ADR-0024 R-4b — import from the previous year (sectioned reports) ──
  // Carries last year's section settings and wording (marked for review), never
  // its figures; replaces what is saved for this report now.
  const [importConfirmOpen, setImportConfirmOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const handleConfirmImport = useCallback(async () => {
    if (!reportId) return;
    setImportConfirmOpen(false);
    setIsImporting(true);
    try {
      const r = await fsaReportService.importFromPriorYear(engagementId, reportId);
      notificationService.toast.success(r.data?.notes ?? 'Imported from the previous year.', 9000);
      onSaved?.();
      refetchHydrate(); // hydrate effect reloads the workspace and re-creates the editors
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not import from the previous year.');
    } finally {
      setIsImporting(false);
    }
  }, [reportId, engagementId, onSaved, refetchHydrate]);

  // ───── full screen · previous year (document layout) ──────────────────────
  const [fullscreen, toggleFullscreen] = useModalFullscreen();
  const loadPreviousYearReport = useCallback(
    () => loadPriorSections(String(engagementId), 'report', hydrate?.reportCode ?? ''),
    [engagementId, hydrate?.reportCode],
  );

  // ───── minimize ───────────────────────────────────────────────────────────
  const { minimize } = useModalMinimize();
  const reportIdRef = useRef(reportId);
  reportIdRef.current = reportId;

  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const currentReportId = reportIdRef.current;
    if (!currentReportId) return;
    const modalId = `${tabId}-fsa-report-${currentReportId}`;
    const headerCode = reportHeader?.code ?? hydrate?.reportCode ?? 'Report';
    const headerName = reportHeader?.name ?? hydrate?.reportName ?? '';
    const savedSnapshot = initialSnapshotRef.current;
    const savedWorkspace = JSON.parse(JSON.stringify(workspaceRef.current)) as ReportWorkspaceData;
    const currentDirty = savedSnapshot ? JSON.stringify(savedWorkspace) !== savedSnapshot : false;
    minimize({
      id: modalId,
      title: `${headerCode} · ${headerName}`,
      moduleName: moduleName ?? 'FSA',
      tabId,
      isDirty: currentDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setWorkspace(savedWorkspace);
        initialSnapshotRef.current = savedSnapshot;
        onRestoreModal?.(currentReportId);
      },
    });
    onClose();
  }, [tabId, moduleName, reportHeader, hydrate?.reportCode, hydrate?.reportName, onClose, onRestoreModal, minimize]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  if (!isOpen) return null;

  const headerCode = reportHeader?.code ?? hydrate?.reportCode ?? '';
  const headerName = reportHeader?.name ?? hydrate?.reportName ?? 'Report';

  return (
    <>
      <div className="modal-backdrop" onClick={handleClose} />
      <div
        className="modal-container modal-xl"
        style={{
          display: 'flex', flexDirection: 'column', maxHeight: '92vh',
          ...(isSections ? { height: '92vh' } : {}),
          ...(fullscreen ? FULLSCREEN_STYLE : {}),
        }}
      >
        {isSaving && (
          <div style={loadingOverlayStyle}>
            <div style={spinnerStyle} />
            <span style={spinnerLabelStyle}>Saving…</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0, flex: 1 }}>
            <IconSystem.file size={16} />
            <h2 className="modal-title" style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {headerName}
              {headerCode && (
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginLeft: '0.5rem', fontWeight: 500 }}>
                  · {headerCode}
                </span>
              )}
            </h2>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <FullscreenButton full={fullscreen} onToggle={toggleFullscreen} disabled={isSaving} />
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Content-mode strip — Template (placeholder flow) vs Free Text
            (auditor types/pastes the complete body). Sits under the header
            so switching doesn't dismiss the modal; the choice is saved
            with the workspace. Not shown for SECTIONS reports: every
            section is already free-form editable. */}
        {!isSections && (
        <div style={modeStripStyle}>
          <button
            type="button"
            style={contentMode === 'TEMPLATE' ? modePillActiveStyle : modePillStyle}
            onClick={() => setContentMode('TEMPLATE')}
            disabled={hydrating || isSaving}
          >
            Template
            <span style={modePillHintStyle}>pinned narrative · fill placeholders</span>
          </button>
          <button
            type="button"
            style={contentMode === 'FREE_TEXT' ? modePillActiveStyle : modePillStyle}
            onClick={() => setContentMode('FREE_TEXT')}
            disabled={hydrating || isSaving}
          >
            Free Text
            <span style={modePillHintStyle}>type or paste the complete report</span>
          </button>
        </div>
        )}

        <div
          className="modal-content"
          style={{
            flex: 1, padding: 0, overflow: 'hidden',
            display: 'flex', flexDirection: 'row', minHeight: 0,
          }}
        >
          {hydrating && !hydrate ? (
            <div style={loadingPaneStyle}>Loading report…</div>
          ) : !hydrate ? (
            <div style={loadingPaneStyle}>Couldn't load report.</div>
          ) : isSections ? (
            <SectionedReportPane
              sectionsJson={hydrate.sectionsJson}
              workspace={workspace}
              setWorkspace={setWorkspace}
              facts={corporateFactsData}
              liveData={hydrate.liveData}
              contentVersion={sectionsContentVersion}
              readOnly={isSaving}
              onSetAuditAnswer={handleSetAuditAnswer}
              signingSide={signingSide}
              loadPreviousYear={loadPreviousYearReport}
              reviewNotes={(
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label htmlFor="report-review-notes" style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                    Reviewer notes <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>— private, never printed</span>
                  </label>
                  <textarea
                    id="report-review-notes"
                    className="form-textarea"
                    value={workspace.notes_text ?? ''}
                    onChange={e => setNotesText(e.target.value)}
                    rows={12}
                    placeholder="Internal review notes for this report"
                    readOnly={isSaving}
                  />
                </div>
              )}
            />
          ) : contentMode === 'FREE_TEXT' ? (
            <>
              {/* LEFT — free-text editor */}
              <div style={leftPaneStyle}>
                <div style={paneHeadingStyle}>
                  Report body
                  <span style={paneHeadingSubStyle}>blank line = paragraph · ALL-CAPS line = heading</span>
                </div>
                <textarea
                  className="form-input"
                  value={workspace.free_text ?? ''}
                  onChange={e => setFreeText(e.target.value)}
                  placeholder="Type or paste the complete report text here…"
                  style={freeTextEditorStyle}
                  spellCheck
                />
                <div style={{ ...notesBlockStyle, margin: '10px 12px 12px 12px' }}>
                  <label style={descriptorLabelStyle}>
                    Reviewer notes
                    <span style={hintStyle}>(private — not printed)</span>
                  </label>
                  <textarea
                    className="form-input"
                    value={workspace.notes_text ?? ''}
                    onChange={e => setNotesText(e.target.value)}
                    rows={2}
                    placeholder="Internal review notes for this report"
                    style={descriptorInputStyle}
                  />
                </div>
              </div>

              {/* RIGHT — free-text preview */}
              <div style={rightPaneStyle}>
                <div style={paneHeadingStyle}>
                  Preview
                  <span style={paneHeadingSubStyle}>updates live</span>
                </div>
                <div style={previewBodyStyle}>
                  <FreeTextPreviewBody text={workspace.free_text ?? ''} resolved={resolvedValues} />
                </div>
              </div>
            </>
          ) : (
            <>
              {/* LEFT — placeholder editor */}
              <div style={leftPaneStyle}>
                <div style={paneHeadingStyle}>
                  Placeholders
                  <span style={paneHeadingSubStyle}>
                    {descriptors.length} declared
                  </span>
                </div>
                {descriptors.length === 0 ? (
                  <div style={emptyHintStyle}>
                    This report has no editable placeholders — the preview shows the body verbatim.
                  </div>
                ) : (
                  <div style={descriptorsListStyle}>
                    {descriptors.map(d => {
                      const code = d.code;
                      if (!code) return null;
                      const auditorValue = workspace.placeholders[code] ?? '';
                      const autoValue = hydrate.autoResolvedValues[code] ?? '';
                      const isAuto = (d.source && d.source !== 'manual') || !!autoValue;
                      const isLong = looksLong(d, code);
                      return (
                        <div key={code} style={descriptorRowStyle}>
                          <label style={descriptorLabelStyle}>
                            {d.label || code}
                            {isAuto && (
                              <span style={autoPillStyle} title={`Auto-resolved from ${d.source ?? 'engagement'}`}>
                                auto
                              </span>
                            )}
                          </label>
                          <code style={tokenCodeStyle}>{`{{${code}}}`}</code>
                          {d.hint && <div style={hintStyle}>{d.hint}</div>}
                          {isLong ? (
                            <textarea
                              className="form-input"
                              value={auditorValue}
                              onChange={e => setPlaceholder(code, e.target.value)}
                              placeholder={autoValue || (d.source === 'manual' ? 'Auditor entry' : '—')}
                              rows={3}
                              style={descriptorInputStyle}
                            />
                          ) : (
                            <input
                              type="text"
                              className="form-input"
                              value={auditorValue}
                              onChange={e => setPlaceholder(code, e.target.value)}
                              placeholder={autoValue || (d.source === 'manual' ? 'Auditor entry' : '—')}
                              style={descriptorInputStyle}
                            />
                          )}
                          {isAuto && autoValue && !auditorValue && (
                            <div style={autoActiveHintStyle}>
                              ↳ using auto: <strong>{autoValue}</strong>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    <div style={notesBlockStyle}>
                      <label style={descriptorLabelStyle}>
                        Reviewer notes
                        <span style={hintStyle}>(private — not printed)</span>
                      </label>
                      <textarea
                        className="form-input"
                        value={workspace.notes_text ?? ''}
                        onChange={e => setNotesText(e.target.value)}
                        rows={3}
                        placeholder="Internal review notes for this report"
                        style={descriptorInputStyle}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* RIGHT — live preview */}
              <div style={rightPaneStyle}>
                <div style={paneHeadingStyle}>
                  Preview
                  <span style={paneHeadingSubStyle}>updates live</span>
                </div>
                <div style={previewBodyStyle}>
                  <ReportPreviewBody
                    bodyHtml={hydrate.bodyHtml}
                    resolved={resolvedValues}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>
            Close
          </button>
          {isSections && hydrate?.hasPriorEngagement && (
            <button
              className="btn btn-secondary"
              onClick={() => setImportConfirmOpen(true)}
              disabled={isSaving || isImporting || !reportId}
              title="Start from last year's report: section settings and wording carry (marked for review); this year's figures fill in fresh"
            >
              <IconSystem.download size={14} /> {isImporting ? 'Importing…' : 'Import from Previous Year'}
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={() => setPdfOpen(true)}
            disabled={isSaving || !reportId || (isSections && isDirty)}
            title={isSections && isDirty
              ? 'Save first — the PDF prints the saved report'
              : 'Open the print-ready PDF preview'}
          >
            <IconSystem.print size={14} /> Print preview
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSaveClick}
            disabled={isSaving || !isDirty}
          >
            Save
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Save Report"
        message="Save the current content of this report?"
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
      <ConfirmDialog
        open={importConfirmOpen}
        title="Import from Previous Year?"
        message={`This replaces what is saved for this report${isDirty ? ' and discards your unsaved changes' : ''} with last year's section settings and wording. Sections with last year's wording are marked for review; this year's figures fill in fresh. Sections no longer in this report version are not carried.`}
        confirmLabel="Import"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleConfirmImport}
        onCancel={() => setImportConfirmOpen(false)}
      />

      {reportId && (
        <FsaReportPdfPreviewModal
          isOpen={pdfOpen}
          engagementId={engagementId}
          reportId={reportId}
          reportLabel={`${headerCode} · ${headerName}`}
          wordFileName={isSections ? `${(headerName || 'Report').replace(/[^A-Za-z0-9 -]/g, '').trim()}.docx` : null}
          onClose={() => setPdfOpen(false)}
        />
      )}
    </>
  );
};

// ===========================================================================
// helpers
// ===========================================================================

function seedWorkspace(hydrate: NonNullable<ReturnType<typeof useFsaReportHydrate>['data']>): ReportWorkspaceData {
  // ADR-0024 — SECTIONS workspace: load the saved blob VERBATIM. A
  // field-by-field rebuild (below) silently drops any key it doesn't name —
  // the defect that erased POLICY-note sections — so pure saved content
  // gets a pure load.
  if (hydrate.contentFormat === 'SECTIONS') {
    let saved: ReportWorkspaceData = { placeholders: {} };
    if (hydrate.workspace?.data) {
      try {
        const parsed = JSON.parse(hydrate.workspace.data);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) saved = { placeholders: {}, ...parsed };
      } catch { /* start fresh */ }
    }
    // Snapshot any field / table value not captured yet (first open, or a
    // chip added since) — saved values stay untouched until Refresh.
    return seedResolvedData(saved, hydrate.liveData);
  }

  // Saved payload wins. When no save exists yet OR a placeholder has no
  // saved value, fall back to the auto-resolved engagement-context value
  // so the auditor sees a sensible starting state. Matches the backend
  // resolver's precedence.
  const saved: ReportWorkspaceData = (() => {
    if (!hydrate.workspace?.data) return { placeholders: {}, notes_text: '' };
    try {
      const parsed = JSON.parse(hydrate.workspace.data) as Partial<ReportWorkspaceData>;
      return {
        placeholders: parsed.placeholders ?? {},
        notes_text: parsed.notes_text ?? '',
        content_mode: parsed.content_mode === 'FREE_TEXT' ? 'FREE_TEXT' : 'TEMPLATE',
        free_text: parsed.free_text ?? '',
      };
    } catch {
      return { placeholders: {}, notes_text: '' };
    }
  })();

  // Auto-resolved values seed any placeholder the auditor hasn't touched.
  // We persist auto-resolved values into the editor state on first open
  // so Save commits the snapshot — once saved, the value is the auditor's
  // (matches the "auto-fill on first open, then fully editable" decision).
  const merged: Record<string, string> = { ...saved.placeholders };
  for (const [k, v] of Object.entries(hydrate.autoResolvedValues ?? {})) {
    if (merged[k] == null || merged[k] === '') merged[k] = v;
  }

  return {
    placeholders: merged,
    notes_text: saved.notes_text,
    content_mode: saved.content_mode ?? 'TEMPLATE',
    free_text: saved.free_text ?? '',
  };
}

/**
 * Converts the pinned narrative's body HTML into free-text seed content:
 * block tags become paragraph breaks, heading text is UPPERCASED so the
 * free-text "ALL-CAPS line = heading" convention preserves the section
 * structure, resolved placeholders are substituted, and unresolved tokens
 * stay as {{TOKEN}} for the PDF resolver to fill or flag.
 */
function templateHtmlToFreeText(bodyHtml: string, resolved: Record<string, string>): string {
  let s = bodyHtml;
  // Uppercase heading bodies BEFORE stripping tags so we know which text
  // was a heading.
  s = s.replace(/<\s*(h[234])(\s[^>]*)?>([\s\S]*?)<\s*\/\s*\1\s*>/gi,
    (_m, _tag, _attrs, inner: string) => `\n\n${inner.replace(/<[^>]+>/g, '').toUpperCase()}\n\n`);
  s = s.replace(/<\s*br\s*\/?\s*>/gi, '\n');
  s = s.replace(/<\s*\/\s*p\s*>/gi, '\n\n');
  s = s.replace(/<[^>]+>/g, '');
  s = s
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, ' ');
  s = s.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (full, token: string) => {
    const v = resolved[token];
    return v != null && v !== '' ? v : full;
  });
  // Collapse 3+ newlines to paragraph breaks and trim edges.
  return s.replace(/\n{3,}/g, '\n\n').split('\n').map(l => l.trim()).join('\n').trim();
}

function looksLong(d: PlaceholderDescriptor, code: string): boolean {
  // Heuristic — long-form fields get a textarea instead of a text input.
  // Templates can hint via a numeric `value_type` field later; for now
  // use a name-based heuristic and label cues that match the seed.
  const tokens = `${d.label ?? ''} ${code}`.toLowerCase();
  return /description|narrative|paragraph|notes?\b|reason|terms|opinion|policy/.test(tokens);
}

// ===========================================================================
// Live preview — small HTML walker
// ===========================================================================

const ReportPreviewBody: React.FC<{
  bodyHtml: string;
  resolved: Record<string, string>;
}> = ({ bodyHtml, resolved }) => {
  // Substitute {{TOKEN}} occurrences with their resolved value, leaving
  // unresolved tokens as a clearly-marked chip so the auditor sees the gap.
  // We work at the string level then render via dangerouslySetInnerHTML
  // because the seed body is trusted template content authored by the
  // system, not user input. Unresolved tokens get a span class that the
  // page CSS / inline style colours amber.
  const html = useMemo(() => {
    return bodyHtml.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (_full, token: string) => {
      const v = resolved[token];
      if (v != null && v !== '') {
        return escapeHtml(v);
      }
      return `<span class="fsa-report-unresolved" style="background:#fef3c7;color:#92400e;padding:1px 6px;border-radius:3px;font-size:0.85em;font-weight:600;">[${escapeHtml(token)}]</span>`;
    });
  }, [bodyHtml, resolved]);

  // eslint-disable-next-line react/no-danger
  return <div className="fsa-report-preview" dangerouslySetInnerHTML={{ __html: html }} />;
};

// ===========================================================================
// Free-text preview — mirrors the backend's ParseFreeTextBlocks rules:
// blank line = paragraph; a short single ALL-CAPS line renders as a bold
// heading; a pasted {{TOKEN}} resolves (unresolved → amber chip).
// ===========================================================================

const FreeTextPreviewBody: React.FC<{
  text: string;
  resolved: Record<string, string>;
}> = ({ text, resolved }) => {
  const paragraphs = useMemo(() => {
    return text
      .replace(/\r\n?/g, '\n')
      .split(/\n{2,}/)
      .map(p => p.trim())
      .filter(p => p.length > 0);
  }, [text]);

  if (paragraphs.length === 0) {
    return <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>(Empty report body.)</div>;
  }

  return (
    <div className="fsa-report-preview">
      {paragraphs.map((para, i) => {
        const isHeading = !para.includes('\n')
          && para.length <= 90
          && /[A-Za-z]/.test(para)
          && !/[a-z]/.test(para);
        const parts = para.split(/(\{\{\s*[A-Za-z0-9_]+\s*\}\})/g);
        return (
          <p
            key={i}
            style={isHeading
              ? { fontWeight: 700, fontSize: '1.02em', marginTop: i === 0 ? 0 : '1em' }
              : { whiteSpace: 'pre-wrap' }}
          >
            {parts.map((part, j) => {
              const m = part.match(/^\{\{\s*([A-Za-z0-9_]+)\s*\}\}$/);
              if (!m) return <React.Fragment key={j}>{part}</React.Fragment>;
              const v = resolved[m[1]];
              if (v != null && v !== '') return <React.Fragment key={j}>{v}</React.Fragment>;
              return (
                <span
                  key={j}
                  style={{ background: '#fef3c7', color: '#92400e', padding: '1px 6px', borderRadius: 3, fontSize: '0.85em', fontWeight: 600 }}
                >
                  [{m[1]}]
                </span>
              );
            })}
          </p>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Content-mode strip styles — same pill pattern as the JE bundle preview
// modal's mode toggle so the two toggles read as one convention.
// ---------------------------------------------------------------------------

const modeStripStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  padding: '0.5rem 1rem',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-primary)',
};
const modePillStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 2,
  padding: '6px 14px',
  border: '1px solid var(--form-border-color)',
  borderRadius: 6,
  background: 'transparent',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  fontSize: '0.8125rem',
  fontWeight: 600,
};
const modePillActiveStyle: React.CSSProperties = {
  ...modePillStyle,
  background: 'var(--primary-color, #2563eb)',
  color: 'white',
  borderColor: 'var(--primary-color, #2563eb)',
};
const modePillHintStyle: React.CSSProperties = {
  fontSize: '0.65rem',
  fontWeight: 400,
  opacity: 0.85,
};
const freeTextEditorStyle: React.CSSProperties = {
  flex: 1,
  minHeight: '18rem',
  margin: '10px 12px 0 12px',
  resize: 'none',
  fontFamily: 'inherit',
  fontSize: '0.85rem',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ===========================================================================
// styles
// ===========================================================================

const loadingOverlayStyle: React.CSSProperties = {
  position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
};
const spinnerStyle: React.CSSProperties = {
  width: '2rem', height: '2rem',
  border: '3px solid var(--border-color)',
  borderTopColor: '#2563eb', borderRadius: '50%',
  animation: 'spin 0.8s linear infinite',
};
const spinnerLabelStyle: React.CSSProperties = {
  fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500,
};
const loadingPaneStyle: React.CSSProperties = {
  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: 'var(--text-secondary)', fontSize: '0.9rem',
};

const leftPaneStyle: React.CSSProperties = {
  flex: '0 0 360px',
  display: 'flex',
  flexDirection: 'column',
  borderRight: '1px solid var(--border-color)',
  background: 'var(--bg-primary)',
  overflow: 'hidden',
};
const rightPaneStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg-secondary)',
  overflow: 'hidden',
};
const paneHeadingStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid var(--border-color)',
  fontSize: '0.78rem',
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'var(--text-secondary)',
  display: 'flex',
  alignItems: 'baseline',
  gap: 8,
};
const paneHeadingSubStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 500,
  textTransform: 'none',
  letterSpacing: 0,
  color: 'var(--text-secondary)',
};

const descriptorsListStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '10px 12px',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};
const descriptorRowStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};
const descriptorLabelStyle: React.CSSProperties = {
  fontSize: '0.78rem',
  fontWeight: 600,
  color: 'var(--text-primary)',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
};
const tokenCodeStyle: React.CSSProperties = {
  fontSize: '0.68rem',
  color: 'var(--text-secondary)',
  fontFamily: 'monospace',
  background: 'var(--bg-secondary)',
  padding: '1px 4px',
  borderRadius: 3,
  alignSelf: 'flex-start',
};
const descriptorInputStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  padding: '4px 8px',
};
const hintStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  color: 'var(--text-secondary)',
};
const autoPillStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '1px 6px',
  borderRadius: 999,
  background: '#dcfce7',
  color: '#166534',
  fontSize: '0.65rem',
  fontWeight: 700,
};
const autoActiveHintStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
};
const emptyHintStyle: React.CSSProperties = {
  padding: '24px 16px',
  color: 'var(--text-secondary)',
  fontSize: '0.85rem',
  fontStyle: 'italic',
  textAlign: 'center',
};
const notesBlockStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  paddingTop: 12,
  borderTop: '1px dashed var(--border-color)',
};

const previewBodyStyle: React.CSSProperties = {
  flex: 1,
  overflow: 'auto',
  padding: '12px 18px',
  background: 'var(--bg-primary)',
  margin: 8,
  borderRadius: 4,
  border: '1px solid var(--border-color)',
  lineHeight: 1.5,
  fontSize: '0.85rem',
  color: 'var(--text-primary)',
};

export default FsaReportModal;
